// services/offline/indexedDB.ts — IndexedDB wrapper for offline-first storage

import type { Tree, UserProfile, Notification } from '@/types';

const DB_NAME = 'foi-db';
const DB_VERSION = 1;

// ── Store names (tables) ──
export const STORES = {
  TREES: 'trees',
  PROFILES: 'profiles',
  NOTIFICATIONS: 'notifications',
  SYNC_QUEUE: 'syncQueue',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

// ── Sync queue entry (for deferred mutations when offline) ──
export interface SyncQueueEntry {
  id: string;        // UUID
  timestamp: string; // ISO
  method: 'POST' | 'PATCH' | 'DELETE';
  url: string;
  body?: unknown;
  retries: number;
}

// ── Database singleton ──
let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Trees store — keyed by tree.id
      if (!db.objectStoreNames.contains(STORES.TREES)) {
        const treesStore = db.createObjectStore(STORES.TREES, { keyPath: 'id' });
        treesStore.createIndex('updatedAt', 'updatedAt');
      }

      // Profiles store — keyed by userId
      if (!db.objectStoreNames.contains(STORES.PROFILES)) {
        const profilesStore = db.createObjectStore(STORES.PROFILES, { keyPath: 'userId' });
        profilesStore.createIndex('nodeId', 'nodeId');
      }

      // Notifications store — keyed by id
      if (!db.objectStoreNames.contains(STORES.NOTIFICATIONS)) {
        const notifStore = db.createObjectStore(STORES.NOTIFICATIONS, { keyPath: 'id' });
        notifStore.createIndex('createdAt', 'createdAt');
        notifStore.createIndex('isRead', 'isRead');
      }

      // Sync queue — keyed by id, ordered by timestamp
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
        syncStore.createIndex('timestamp', 'timestamp');
      }
    };
  });

  return dbPromise;
}

// ── Generic get by key ──
async function get<T>(store: StoreName, key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

// ── Generic put (upsert) ──
async function put<T>(store: StoreName, value: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).put(value);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ── Generic getAll ──
async function getAll<T>(store: StoreName): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

// ── Generic delete ──
async function remove(store: StoreName, key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ── Public API ──

export const idb = {
  // Trees
  getTree: (id: string) => get<Tree>(STORES.TREES, id),
  putTree: (tree: Tree) => put<Tree>(STORES.TREES, tree),
  getAllTrees: () => getAll<Tree>(STORES.TREES),

  // Profiles
  getProfile: (userId: string) => get<UserProfile>(STORES.PROFILES, userId),
  putProfile: (profile: UserProfile) => put<UserProfile>(STORES.PROFILES, profile),

  // Notifications
  getAllNotifications: () => getAll<Notification>(STORES.NOTIFICATIONS),
  putNotification: (n: Notification) => put<Notification>(STORES.NOTIFICATIONS, n),
  removeNotification: (id: string) => remove(STORES.NOTIFICATIONS, id),

  // Sync Queue
  getAllSyncQueue: () => getAll<SyncQueueEntry>(STORES.SYNC_QUEUE),
  enqueueMutation: (entry: SyncQueueEntry) => put<SyncQueueEntry>(STORES.SYNC_QUEUE, entry),
  dequeueMutation: (id: string) => remove(STORES.SYNC_QUEUE, id),
};
